import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core'
import {
  BookingMultiDayForm,
  BookingRoomsFormValue
} from 'src/app/shared/models'
import moment from 'moment-mini'
import { Moment } from 'moment-mini'
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { environment } from '../../../../../environments/environment'
import { AppState } from '../../../../core/core.state'
import { Store } from '@ngrx/store'
import { productSelectors } from '../../../product/product.selectors'
import { Product } from '../../../product/product'
import { Dictionary } from '@ngrx/entity'
import { bookingSelectors } from '../../../booking/booking.selectors'
import { combineLatest, Subject } from 'rxjs'
import { ConfigurationService } from '../../../../shared/services/configuration.service'
import { BookingMultiDayService } from '../booking-multi-day.service'
import { Booking } from '../../../booking/booking'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { markFormGroupDirty } from '../../../../shared/utils/mark-form-dirty'
import { TranslateService } from '@ngx-translate/core'
import { NotificationService } from 'src/app/core/notification.service'
import { distinctUntilChanged, take, withLatestFrom } from 'rxjs/operators'
import { Actions } from '@ngrx/effects'
import { OnChanges } from '@angular/core'
import { ParticipationService } from 'src/app/features/booking/participation/participation.service'
import { participationSelectors } from 'src/app/features/booking/participation/participation.selectors'
import { Participation } from 'src/app/features/booking/participation/participation'

@UntilDestroy()
@Component({
  selector: 'step-02-rooms-form',
  templateUrl: './step02-rooms-form.component.html',
  styleUrls: ['./step02-rooms-form.component.scss']
})
export class Step02RoomsFormComponent implements OnInit, OnChanges {
  @Input() title: string

  @Input() datesList: Moment[]

  @Input() products: Partial<Product>[] = []

  @Output() onSubmit = new EventEmitter<any>()

  @Input() bookingRooms: BookingRoomsFormValue

  @Input() booking: BookingMultiDayForm
  public isLoading$ = this.store.select(productSelectors.selectIsLoading)
  public form: UntypedFormGroup
  public products$: Subject<any> = new Subject<any>()
  public errorBookeableProduct: boolean = false
  public selectedDate: Moment
  public totalProductsIds: number
  public staticAssetBaseUrl: string = environment.staticAssetBaseUrl
  public productEntities$ = this.store.select(productSelectors.selectEntities)
  public bookingEntities$ = this.store.select(bookingSelectors.selectEntities)
  public bookings: Booking[] = []
  public selectedDateIndex: number = 0
  public wholeStayStatus: boolean = false
  public datesProductsMap = new Map<
    string,
    { resourceProductId: number; activityProductId: number }[]
  >()
  public productBookingEntities$ = combineLatest([
    this.productEntities$,
    this.bookingEntities$
  ])
  public bookingsSleepingOverAvailable: Booking[]

  constructor(
    private fb: UntypedFormBuilder,
    private actions$: Actions,
    private store: Store<AppState>,
    public configurationService: ConfigurationService,
    public bookingMultiDayService: BookingMultiDayService,
    private translate: TranslateService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private notificationService: NotificationService,
    public participationService: ParticipationService
  ) {}

  public get datesForm(): UntypedFormArray {
    if (this.form) {
      return this.form?.get('dates') as UntypedFormArray
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.products &&
      JSON.stringify(changes.products.currentValue) !==
        JSON.stringify(changes.products.previousValue)
    ) {
      this.products = changes.products.currentValue
      console.log('selected products for the step ', this.products)
    }
  }

  ngOnInit(): void {
    // set first date as selected
    this.selectedDate = this.datesList[0]

    this.form = this.fb.group({
      dates: new UntypedFormArray([]),
      checkIn: [null, Validators.required],
      checkOut: [null, Validators.required]
    })

    this.productBookingEntities$
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.store.select(participationSelectors.selectAll)),
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b))
      )
      .subscribe(([[productEntities, bookingEntities], participations]) => {
        Object.values(bookingEntities).filter(
          (bookings) =>
            bookings.linkedProductId ==
            this.booking.details.productIdSleepingOver
        )
        // .map((booking) => (bookingsSleepingOver[booking.id] = booking))
        this.bookingsSleepingOverAvailable = Object.values(
          bookingEntities
        ).filter(
          (bookings) =>
            bookings.linkedProductId ==
            this.booking.details.productIdSleepingOver
        )

        this.errorBookeableProduct = false
        this.datesList
          .filter((d) => d && moment(d).isValid())
          .forEach((date) => {
            this.addDateToDatesProductForm(
              date,
              productEntities,
              bookingEntities,
              participations
            )
          })
        // this.changeDetectorRef.detectChanges()
      })
  }

  public getDatesForm(selectedDate: Moment): UntypedFormGroup {
    let formGroup: UntypedFormGroup
    this.datesForm.controls.forEach((control: UntypedFormGroup) => {
      selectedDate = selectedDate ?? moment(control.value['date'])
      if (
        moment(control.value['date']).format('YYYY-MM-DD') ===
        selectedDate?.format?.('YYYY-MM-DD')
      ) {
        formGroup = control
      }
    })
    return formGroup
  }

  public getDateProductsFormArray(selectedDate: Moment): UntypedFormArray {
    return this.getDatesForm(selectedDate)?.get('products') as UntypedFormArray
  }

  public getDateProductFormGroup(
    selectedDate: Moment,
    activityProductId: number,
    resourceProductId: number
  ): UntypedFormGroup | null {
    const productsFormArray = this.getDateProductsFormArray(selectedDate)

    if (!productsFormArray?.controls?.length) {
      return null
    }

    return (
      productsFormArray.controls.find(
        (control: UntypedFormGroup) =>
          control.get('productId')?.value === activityProductId &&
          control.get('bookableProductId')?.value === resourceProductId
      ) ?? null
    ) as UntypedFormGroup | null
  }

  public isSharedResourceDisabled(
    selectedDate: Moment,
    activityProductId: number,
    resourceProductId: number
  ): boolean {
    return Boolean(
      this.getDateProductFormGroup(
        selectedDate,
        activityProductId,
        resourceProductId
      )
        ?.get('persons')
        ?.disabled
    )
  }

  private syncSharedResourceDisabledStateForDate(selectedDate: Moment): void {
    const productsFormArray = this.getDateProductsFormArray(selectedDate)

    if (!productsFormArray?.controls?.length) {
      return
    }

    const selectedResources = new Set(
      productsFormArray.controls
        .filter((control: UntypedFormGroup) => Number(control.get('persons')?.value) > 0)
        .map((control: UntypedFormGroup) => control.get('bookableProductId')?.value)
        .filter(Boolean)
    )

    productsFormArray.controls.forEach((control: UntypedFormGroup) => {
      const personsControl = control.get('persons')
      const resourceProductId = control.get('bookableProductId')?.value
      const hasOwnSelection = Number(personsControl?.value) > 0
      const shouldDisable =
        !hasOwnSelection && selectedResources.has(resourceProductId)

      if (shouldDisable && personsControl?.enabled) {
        personsControl.disable({ emitEvent: false })
      } else if (!shouldDisable && personsControl?.disabled) {
        personsControl.enable({ emitEvent: false })
      }
    })
  }

  private syncSharedResourceDisabledStateForAllDates(): void {
    this.datesList
      .filter((date) => date && moment(date).isValid())
      .forEach((date) => this.syncSharedResourceDisabledStateForDate(date))
  }

  public removeDate(dateIndex: number) {
    const selectedDate = this.datesList[dateIndex]
    this.datesList.splice(dateIndex, 1)
    this.removeDatesFormByDate(selectedDate)
    this.selectedDate = this.datesList[0]
  }

  public removeDatesFormByDate(selectedDate: Moment): void {
    this.datesForm.controls.forEach(
      (control: UntypedFormGroup, index: number) => {
        if (control.value['date'] === selectedDate?.format?.()) {
          this.datesForm.removeAt(index)
        }
      }
    )
  }

  private getDefaultRoomBoundaryTime(
    rawTime: string,
    fallbackTime: string
  ): Moment {
    const parsedTime = moment(`2020-01-01 ${rawTime}`, [
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD HH:mm:ss'
    ])

    return parsedTime.isValid()
      ? parsedTime
      : moment(fallbackTime, 'YYYY-MM-DD HH:mm:ss')
  }

  public addDateToDatesProductForm(
    selectedDate: Moment,
    productEntities: Dictionary<Product>,
    bookingEntities: Dictionary<Booking>,
    participations?: Participation[]
  ) {
    if (!selectedDate || !moment(selectedDate).isValid()) {
      return
    }
    const dateFormGroupControlConfig = {
      note: [null],
      date: [selectedDate.format()],
      products: new UntypedFormArray([])
    }
    const dateFormGroup = this.fb?.group(dateFormGroupControlConfig, {})

    const productFormArray = dateFormGroup.get('products') as UntypedFormArray

    this.bookings = []
    const activityIdResourceIdPairs: {
      activityProductId: number
      resourceProductId: number
    }[] = []

    for (const activityProductId of this.products) {
      const activityProduct = productEntities[activityProductId.id]

      if (
        !activityProduct?.id ||
        !activityProduct.parentBookableProducts?.length
      ) {
        continue
      }

      for (const parentBookableProductId of activityProduct.parentBookableProducts) {
        // maintain local booking caching for availability checking and form filling
        const productBookings = Object.values(bookingEntities).filter(
          (booking) => booking.productId === parentBookableProductId
        )

        if (productBookings?.length) {
          productBookings.map((booking) => {
            const startDate = moment(booking.startDate)
            const endDate = moment(booking.endDate)
            const selectDate = selectedDate
            let isSameOrAfter: boolean = false
            let isSameOrBefore: boolean = false
            isSameOrBefore = moment(startDate).isSameOrBefore(
              moment(selectDate).clone().endOf('day')
            )
            isSameOrAfter = moment(endDate).isAfter(
              moment(selectDate).clone().endOf('day')
            )

            if (isSameOrAfter && isSameOrBefore) {
              this.bookings.push(booking)
            }
          })
        }

        // build the room form rows
        const resource = productEntities[parentBookableProductId]

        if (!resource?.id) {
          continue
        }

        if (this.form.get('checkIn').value === null) {
          const rawDefaultStartTime =
            typeof activityProduct.defaultStartTime === 'string'
              ? activityProduct.defaultStartTime
              : moment(activityProduct.defaultStartTime).format('HH:mm:ss')
          const rawDefaultEndTime =
            typeof activityProduct.defaultEndTime === 'string'
              ? activityProduct.defaultEndTime
              : moment(activityProduct.defaultEndTime).format('HH:mm:ss')
          const date = this.getDefaultRoomBoundaryTime(
            rawDefaultStartTime,
            '2020-01-01 00:00:00'
          )
          const endDate = this.getDefaultRoomBoundaryTime(
            rawDefaultEndTime,
            '2020-01-01 23:59:00'
          )

          if (date.isValid() && endDate.isValid()) {
            this.form.controls.checkIn.setValue(date.toDate())
            this.form.controls.checkOut.setValue(endDate.toDate())
          }
        }

        let maxPart: any = this.participationService.calculateParticipantLimits(
          activityProduct,
          null,
          null
        ).maxParticipants

        // here we have to deduct the participations of the parentBookableProducts
        this.bookings.forEach((booking) => {
          if (booking.productId === parentBookableProductId) {
            maxPart -= booking.currentParticipantCount
              ? booking.currentParticipantCount
              : booking?.participations?.length
                ? booking?.participations?.length
                : participations?.filter((pt) => pt.bookingId == booking.id)
                    ?.length
          }
        })

        activityIdResourceIdPairs.push({
          activityProductId: activityProduct.id,
          resourceProductId: resource.id
        })

        productFormArray.push(
          this.fb.group({
            productId: activityProduct.id,
            bookableProductId: resource.id,
            qty: [1],
            persons: [
              0,
              [Validators.required, Validators.min(0), Validators.max(maxPart)]
            ],
            maxResourceParticipants: [maxPart]
          })
        )
      }
    }

    this.totalProductsIds = productFormArray.length
    this.datesProductsMap.set(selectedDate.format(), activityIdResourceIdPairs)

    console.log('datesProductsMap ', this.datesProductsMap)

    let existDate = false
    this.datesForm.controls.map((ct: any) => {
      if (
        moment(ct.controls['date'].value).format('YYYY-MM-DD') ===
        moment(dateFormGroup.controls['date'].value).format('YYYY-MM-DD')
      ) {
        ct.controls['products'] = dateFormGroup.controls['products']
        existDate = true
      }
    })

    if (!existDate) {
      this.datesForm.push(dateFormGroup)
    }

    this.syncSharedResourceDisabledStateForDate(selectedDate)
  }

  onSubmitFormValues($event) {
    $event.preventDefault()

    if (this.form.invalid) {
      markFormGroupDirty(this.form)
      return
    }

    const form = this.form.getRawValue()

    for (let date of form.dates) {
      const totalNumberOfPersons = date.products
        .map((product) => product.persons)
        .reduce((sum: number, number: number) => sum + number, 0)

      if (
        totalNumberOfPersons > this.booking.details.participants.numberSleepOver
      ) {
        this.translate
          .get('bookingModule.tooManyPersonOnDate')
          .pipe(take(1))
          .subscribe((message) => {
            this.notificationService.emit(
              message.replace(
                '%DATE%',
                moment(date.date).format('DD MMM, YYYY')
              ),
              'warning'
            )
          })
        return
      }
    }

    this.onSubmit.emit(this.form.getRawValue())
  }

  public copyFromLastDay(dateIndex: number): void {
    const lastDay = dateIndex - 1
    if (lastDay >= 0) {
      const currentValue = this.getDatesForm(
        this.datesList[dateIndex]
      ).getRawValue()
      const previousDayFormValue = this.getDatesForm(
        this.datesList[lastDay]
      ).getRawValue()

      this.getDatesForm(this.datesList[dateIndex]).setValue({
        ...previousDayFormValue,
        date: currentValue.date
      })
      this.syncSharedResourceDisabledStateForDate(this.datesList[dateIndex])
    }
  }
  onDateClick(date, index) {
    this.selectedDateIndex = index
    this.selectedDate = date
  }
  public setChangeValue() {
    this.syncSharedResourceDisabledStateForAllDates()
    this.changeDetectorRef.detectChanges()
  }
  public getDatesFormForWHoleStay(selectedDate: Moment): UntypedFormGroup {
    let formGroup: UntypedFormGroup
    this.datesForm.controls.forEach((control: UntypedFormGroup) => {
      selectedDate = selectedDate ?? moment(control.value['date'])
      if (
        moment(control.value['date']).format('YYYY-MM-DD') ===
        selectedDate?.format?.('YYYY-MM-DD')
      ) {
        formGroup = control
      }
    })
    return formGroup
  }
  public setForWholeStay(dateIndex: number) {
    this.wholeStayStatus = !this.wholeStayStatus
    if (this.wholeStayStatus) {
      const formValue = this.getDatesFormForWHoleStay(
        this.datesList[dateIndex]
      ).getRawValue()

      this.datesList.forEach((date, index) => {
        if (dateIndex !== index) {
          this.getDatesFormForWHoleStay(this.datesList[index]).setValue({
            ...formValue,
            date: date.format()
          })
        }
      })
    }
  }

  public getTotalPersons(controls: UntypedFormGroup, index?: number): number {
    let totalPerson: number = controls?.getRawValue()?.products?.reduce(
      (prv, curr) => prv + curr.persons,
      0
    )
    return totalPerson
  }

  public addDateBefore() {
    const firstDate: Moment = moment(this.datesList[0])
    firstDate.subtract(1, 'days')
    this.datesList.unshift(firstDate)

    this.productBookingEntities$
      .pipe(untilDestroyed(this))
      .subscribe(([productEntities, bookingEntities]) => {
        this.addDateToDatesProductForm(
          firstDate,
          productEntities,
          bookingEntities
        )
      })
    this.selectedDate = this.datesList[0]
  }

  public addDateAfter() {
    const lastDate: Moment = moment(this.datesList[this.datesList.length - 1])
    lastDate.add(1, 'days')
    this.datesList.push(lastDate)

    this.productBookingEntities$
      .pipe(untilDestroyed(this))
      .subscribe(([productEntities, bookingEntities]) => {
        this.addDateToDatesProductForm(
          lastDate,
          productEntities,
          bookingEntities
        )
      })
    this.selectedDate = this.datesList[this.datesList.length - 1]
  }

  private sortProductsByMaxParticipants(productEntities: Dictionary<Product>) {
    return (a, b) => {
      const maxPartA = a.parentBookableProducts
        .map((id) => productEntities[id])
        .reduce((p, c) => p + c.maxParticipants, 0)
      const maxPartB = b.parentBookableProducts
        .map((id) => productEntities[id])
        .reduce((p, c) => p + c.maxParticipants, 0)

      return maxPartB - maxPartA
    }
  }
}
